import { useTranslation } from '../../../../lib/i18n';
import { decisionOutcomeCopy } from '../../../../lib/goal-ledger';
import { isOpenDecision, meetingApprovalAskerLine, type MeetingApprovalRow } from '../../../../lib/meeting-approval';
import { useDecisionAnswer } from '../../../../lib/use-decision-answer';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { useChatSurfaceVisible } from '../../../../lib/chat-surface-visible';
import { DecisionAnswerControls } from '../../goals/DecisionAnswerBlock';
import { Card } from '../../ui';
import styles from './MeetingApprovalCard.module.css';

/**
 * The owner's Approve / Decline for a question a guest asked in a live meeting.
 *
 * It cannot be choice chips: a chip answer becomes a USER turn, and a model cannot cast a verdict. So the
 * control is the ledger's own `DecisionAnswerControls` over `useDecisionAnswer`, on the same
 * `POST /api/decision-verdict` path — first answer wins, whether it comes from here, another device, or
 * the owner's WhatsApp reply. A decision that is no longer open shows the ledger's settled sentence.
 *
 * The decision has no goal and no task (its scope is the meeting session), so none is passed.
 */
export function MeetingApprovalCard({ row }: { row: MeetingApprovalRow }) {
  const { t } = useTranslation();
  const flow = useDecisionAnswer(row.decision, undefined, undefined);
  const asker = meetingApprovalAskerLine(row.meetingApproval);
  const questionId = `meetingApproval-${row.decision.id}-question`;
  const settled = isOpenDecision(row) ? undefined : decisionOutcomeCopy(row.decision);
  // The denominator for Approve / Decline (logged with location `meeting_approval`). The row carries no
  // conversation or meeting session id, so the decision id rides in additional fields; `state` tells an
  // open card the owner can still answer from a settled one.
  const surfaceVisible = useChatSurfaceVisible();
  const seenRef = useItemSeenBi('meeting-approval-card', {
    additionalFields: { decision_id: row.decision.id, state: settled ? 'settled' : 'open' },
    disabled: !surfaceVisible,
  });
  return (
    <Card ref={seenRef} variant="outline" size="sm" className={styles.card} data-testid={`meetingApproval-${row.decision.id}`}>
      <span className={styles.asker} dir="auto">
        {t(asker.key, asker.params)}
      </span>
      <span id={questionId} className={styles.question} dir="auto">
        {row.meetingApproval.askedQuestion}
      </span>
      {settled ? (
        <span className={styles.settled}>{t(settled.key, settled.params)}</span>
      ) : (
        <DecisionAnswerControls
          decision={row.decision}
          questionId={questionId}
          index={0}
          flow={flow}
          biPage="chat"
          biLocation="meeting_approval"
          noteOnApprove
        />
      )}
    </Card>
  );
}
